import _ from 'lodash';
import * as Sentry from '@sentry/react';
import {
  getCheckingActiveFilter,
  getFormattedSearchValue,
  getSearchMatches,
  getServiceDropUp,
  getCategoryDropUp,
  getCategoryActionList,
  getServiceActionList,
  checkIsStrictSearch,
  isStyleDisableCategory,
} from './getters';
import { store } from '../../../../store';
import { modifiersProvidersToObject } from './modifiers';

/**
 *
 * @name modifierListAfterFiltersSearch
 * @description Лист (без категорий), пропускаем через поиск и фильтры
 * @param {array} list
 * @param {object} filters
 * @param {string|null}search
 * @param {string|null}searchOption
 * @returns {array}
 */
export const modifierListAfterFiltersSearch = (list, filters, search, searchOption) => {
  if (list) {
    let listAfterFilter = list;
    const activeFilter = getCheckingActiveFilter(filters);
    if (activeFilter || search) {
      listAfterFilter = list.reduce((filtered, category) => {
        /*
          Если категория с ошибками, не фильтруем по ней
         */
        if (!category.hasOwnProperty('title') || !category.hasOwnProperty('id')) {
          return filtered;
        }

        /*
         Поиск по категориям
        */
        if (search && !searchOption && getFormattedSearchValue(search) === getFormattedSearchValue(category.title)) {
          filtered.push(category)
          return filtered;
        }
        const isStrictSearch = checkIsStrictSearch(searchOption)
        if (search && searchOption === 'category-name') {
          const categoryTitle = getFormattedSearchValue(category.title);
          const isMatch = getSearchMatches(search, [categoryTitle], isStrictSearch)
          if (isMatch) {
            filtered.push(category)
            return filtered;
          }
          return filtered;
        }

        const servicesAfterFilter = category.services.filter((service) => {
          const isFiltered = Object.values(filters).find((filter) => !filter.func(service, filter.value));
          return !isFiltered;
        });
        if (servicesAfterFilter.length) {
          if (search) {
            const getSearchValues = (service) => {
              if (searchOption) {
                switch (searchOption) {
                case 'service-id':
                  return [getFormattedSearchValue(service.id)]
                case 'service-name':
                  return [getFormattedSearchValue(service.name)]
                case 'external-id':
                  return [getFormattedSearchValue(service.provider_service_id ? service.provider_service_id : '')]
                default:
                  return []
                }
              }
              return [
                getFormattedSearchValue(service.id),
                getFormattedSearchValue(service.name),
                getFormattedSearchValue(service.provider_service_id ? service.provider_service_id : ''),
              ]
            };
            const searchServices = servicesAfterFilter.filter((service) => {
              const searchValues = getSearchValues(service);
              return getSearchMatches(search, searchValues, isStrictSearch)
            });
            if (searchServices.length) {
              filtered.push({
                ...category,
                services: searchServices,
              })
            }
          } else {
            filtered.push({
              ...category,
              services: servicesAfterFilter,
            })
          }
        }
        return filtered;
      }, []);
    }
    return listAfterFilter;
  }
  return list;
}

/**
 *
 * @name modifierSplittingRows
 * @description Делаем из категорий и сервисов один список
 * @param {array} categories - Список категория с вложенными сервисами
 * @param {object} arg - Дополнительные параметры
 * @returns {Error|[]}
 */
export const modifierSplittingRows = (categories, arg = {}) => {
  const { services: { servicesChecked, providers, config } } = store.getState();
  const providerObject = modifiersProvidersToObject(providers);
  const list = categories;

  const options = {
    withOutShowHideCategory: arg.withOutShowHideCategory || false,
    highlightServices: arg.highlightServices || [],
    highlightCategories: arg.highlightCategories || [],
    search: arg.search || null,
    filters: arg.filters || false,
  }

  const { withOutShowHideCategory, highlightServices, highlightCategories, filters } = options;

  if (list && _.isArray(list)) {
    const rows = [];
    const isCategoriesCollapsed = !!list.some((category) => category.show);
    const isActiveFilters = Object.values(filters).some((filter) => !_.isNull(filter.value))
    const listLength = list.length;

    list.forEach((category, categoryIndex) => {
      const { id, visibility, title, show, type, icon } = category;
      /*
        Правка, если не приходят категории, то не показывать сервисы внутри нее
        Таска: https://users.scrummate.com/#story/2123430
       */
      if (!category.hasOwnProperty('title') || !category.hasOwnProperty('id')) {
        if (!window.appConfig.is_dev) {
          console.error(JSON.stringify(category))
        }
        Sentry.captureMessage('Category services is empty');
        return;
      }
      const highlight = highlightCategories.length ? highlightCategories.includes(id) : false;
      const actions = getCategoryActionList(category, config);
      const isDropdownDropUp = getCategoryDropUp(category, categoryIndex, listLength, category.services.length);
      const isLongTypeName = [3, 4, 7, 8, 14, 20, 101].includes(type);
      const isNextCategoryShow = list[categoryIndex + 1] && list[categoryIndex + 1].show;
      // Стиль категории как у задизейбленной
      const isStyleDisable = isStyleDisableCategory(category);
      rows.push({
        id,
        icon,
        visibility,
        isStyleDisable,
        show,
        name: title,
        collectionId: null,
        collection: 'category',
        servicesLength: category.services.length,
        isDropdownDropUp,
        categoryIndex,
        isActiveFilters,
        isLongTypeName,
        isCategoriesCollapsed,
        highlight,
        actions,
      });

      if (withOutShowHideCategory || show) {
        category.services.forEach((service, serviceIndex) => {
          const id = Number(service.id);
          const isChecked = servicesChecked.length ? servicesChecked.includes(id) : false;
          const provider = providerObject[service.provider_id] ? providerObject[service.provider_id] : null;
          const actions = getServiceActionList(service, config);
          const highlight = highlightServices.length ? highlightServices.includes(id) : false;
          const isVisibilityService = !visibility ? false : (!!service.status);
          const isDropdownDropUp = getServiceDropUp(categoryIndex, listLength, serviceIndex, category.services.length, isNextCategoryShow, list);
          rows.push({
            ...service,
            collectionId: category.id,
            collection: 'service',
            checked: isChecked,
            visibility: isVisibilityService,
            isDropdownDropUp,
            isActiveFilters,
            highlight,
            provider,
            categoryIndex,
            serviceIndex,
            actions,
          })
        });
      }
    })
    return rows;
  }
  return new Error('categories not found');
};

/**
 *
 * @name modifierServicesListFilter
 * @description - Из категорий делаем единый листинг с сервисами
 * @param {array} startList - Список с кагоериями и вложенными сервисами
 * @param {object} filters - Фильтры
 * @param {string} search - Значение из поля поиск
 * @param {string} searchOption - Опции для поиска
 * @param {array} highlightServices - Массив ID сервисов которые надо подсветить
 * @param {array} highlightCategories - Массив ID категорий которые надо подсветить
 * @returns {Error|[]}
 */
export const modifierServicesListFilter = (startList, filters, search, searchOption, highlightServices, highlightCategories) => {
  if (startList) {
    const listAfterFilter = modifierListAfterFiltersSearch(startList, filters, search, searchOption);
    return modifierSplittingRows(listAfterFilter, {
      filters,
      highlightServices,
      highlightCategories,
      search,
    });
  }
  return new Error('List not found');
};
